import { useEffect } from "react";

import { markActive } from "../auth/activity";

/**
 * Record real interaction, for the idle check in `useWrite` (S0.2 / DA-3336).
 *
 * Mounted ONCE, in the app shell. Per-component listeners would multiply with
 * the tree and each pay the same cost for the same stamp.
 *
 * WHAT COUNTS AS ACTIVITY is the whole design of this hook. Not render — a
 * re-render happens on its own and proves nobody is there. Not a timer, for
 * the same reason. Not mouse movement either: a sleeping laptop with a
 * trackpad under a cable can emit those for hours, which would defeat the
 * rule silently.
 *
 * Keyboard, pointer-down and visibility-return are deliberate acts. A tab
 * becoming visible is the weakest of the three, but an operator returning to
 * the tab genuinely is present, and without it the first click after
 * switching back refuses for no reason the operator can see.
 *
 * Listeners are PASSIVE and on the capture phase, so nothing here can block
 * or reorder an event the UI depends on.
 */
export function useActivityTracking(): void {
  useEffect(() => {
    const touch = () => markActive();

    const onVisible = () => {
      if (document.visibilityState === "visible") markActive();
    };

    // Seed on mount: loading the console IS activity, and leaving the stamp
    // null would make the very first write re-authenticate.
    markActive();

    window.addEventListener("keydown", touch, { capture: true, passive: true });
    window.addEventListener("pointerdown", touch, { capture: true, passive: true });
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      window.removeEventListener("keydown", touch, { capture: true });
      window.removeEventListener("pointerdown", touch, { capture: true });
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);
}
